// DesertFooter — the compact footer for the Desert Book routes (the front
// door and the stories; see src/lib/theme.ts). AppShell renders it there in
// place of SiteFooter, whose sage bands belong to the rest of the site.
//
// Two bands on rock-black: the mark with a home link and four onward doors,
// then a mono strip with the © line and the Duotone credit the font license
// asks for (the titles on these pages are set in Duotone).
//
// Full form (inner Desert Book pages; the front door and the stories keep the
// compact form, see isCompactFooter in theme.ts): the horizon line, the mark
// and release line beside the shared site map, a quiet stay-current row, then
// the same strip (Chris, 2026-10-27).
//
// Mobile: the doors wrap; the site map folds into <details> rows; side
// gutters fall to 16px at phone width.

import { Link, useLocation } from "wouter";
import { HORIZON, lastLineFor } from "../lib/theme";
import Trail from "./Trail";
import FooterSiteMap from "./FooterSiteMap";
import { ENGAGEMENT_PILLS } from "./cta";
import { LinkMark } from "./icons";

/* ── Footer colors (inline, like SiteFooter's sage constants) ── */

const ROCK = "#21181A"; // = desert --ink
const SAND_TEXT = "#e8d9c0"; // 12.5:1 on ROCK
const SAND_DIM = "#b8a68a"; // 7.3:1 on ROCK
const SAND_LINE = "#3a2e2a"; // hairline divider
const SUN = "#d9a35f"; // link accent, 7.7:1 on ROCK

const GUTTER = "clamp(16px, 4vw, 48px)";

const DOORS = [
  { label: "Start a guided path", href: "/start" },
  { label: "The protocol", href: "/learn" },
  { label: "Writing", href: "/writing" },
  { label: "About", href: "/about" },
];

const MARK = (
  <span aria-hidden style={{ width: 22, height: 22, border: `1.5px solid ${SAND_TEXT}`, borderRadius: "50%", position: "relative", display: "inline-block", flexShrink: 0 }}>
    <span style={{ position: "absolute", inset: 5, border: `1px solid ${SAND_TEXT}`, borderRadius: "50%" }} />
  </span>
);

const SMALL_LINK: React.CSSProperties = { color: SAND_TEXT, textDecoration: "underline", textDecorationColor: "rgba(232, 217, 192, 0.35)", textUnderlineOffset: 3 };

/** The full top band: the mark and release line beside the site map, then the stay-current row. */
function FullTop() {
  const live = ENGAGEMENT_PILLS.filter((p) => p.kind === "live");
  return (
    <div className="desert-footer-full" style={{ padding: `48px ${GUTTER} 36px` }}>
      <div style={{ maxWidth: "var(--frame-w)", margin: "0 auto" }}>
        <div className="site-footer-top" style={{ display: "grid", gridTemplateColumns: "minmax(220px, 0.8fr) minmax(0, 2.4fr)", gap: 48, marginBottom: 32 }}>
          <div>
            <Link href="/" aria-label="KTP home" style={{ display: "inline-flex", alignItems: "center", gap: 10, color: SAND_TEXT, textDecoration: "none", fontFamily: "var(--mono)", fontWeight: 600, fontSize: 14, letterSpacing: 1.2 }}>
              {MARK}
              KTP/
            </Link>
            <p className="desert-footer-tagline" style={{ margin: "16px 0 14px", fontSize: 14, lineHeight: 1.6, color: SAND_TEXT, maxWidth: 300 }}>
              Trust in motion — across code, classrooms, institutions, and minds. KTP is what governance looks like when machines do the acting.
            </p>
            <p style={{ margin: 0, fontFamily: "var(--mono)", fontSize: 11, color: SAND_DIM }}>
              rfc v2.1.0 · gödel ·{" "}
              <a href="https://github.com/nmcitra/ktp-rfc/releases/tag/v2.1.0" target="_blank" rel="noopener noreferrer" style={{ ...SMALL_LINK, color: SUN }}>
                release notes<LinkMark />
              </a>
            </p>
          </div>
          <FooterSiteMap palette={{ head: SAND_TEXT, text: SAND_TEXT, dim: SAND_DIM, line: SAND_LINE }} />
        </div>
        <div style={{ borderTop: `1px solid ${SAND_LINE}`, paddingTop: 18, display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "10px 22px", fontSize: 14 }}>
          <span style={{ fontFamily: "var(--mono)", fontSize: 10, letterSpacing: 1.2, textTransform: "uppercase", color: SAND_DIM }}>Stay current</span>
          {live.map((p) => (
            <a key={p.label} href={p.href} target="_blank" rel="noopener noreferrer" style={SMALL_LINK}>
              {p.label}<LinkMark />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function DesertFooter({ full = false }: { full?: boolean }) {
  const [location] = useLocation();
  const lastLine = lastLineFor(location);
  return (
    <footer
      style={{
        marginTop: "auto",
        background: ROCK,
        color: SAND_TEXT,
        fontFamily: "var(--sans)",
      }}
    >
      {full && <div aria-hidden style={{ height: 2, background: HORIZON }} />}
      {full ? <FullTop /> : (
      <div style={{ padding: `40px ${GUTTER} 32px` }}>
        <div
          style={{
            maxWidth: "var(--frame-w)",
            margin: "0 auto",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "20px 40px",
          }}
        >
          <Link
            href="/"
            aria-label="KTP home"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              color: SAND_TEXT,
              textDecoration: "none",
              fontFamily: "var(--mono)",
              fontWeight: 600,
              fontSize: 14,
              letterSpacing: 1.2,
            }}
          >
            {/* The same two-ring mark as SiteFooter's brand block. */}
            <span
              aria-hidden
              style={{
                width: 22,
                height: 22,
                border: `1.5px solid ${SAND_TEXT}`,
                borderRadius: "50%",
                position: "relative",
                display: "inline-block",
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  position: "absolute",
                  inset: 5,
                  border: `1px solid ${SAND_TEXT}`,
                  borderRadius: "50%",
                }}
              />
            </span>
            KTP/
          </Link>
          <nav aria-label="Onward">
            <ul
              style={{
                listStyle: "none",
                margin: 0,
                padding: 0,
                display: "flex",
                flexWrap: "wrap",
                gap: "12px 28px",
              }}
            >
              {DOORS.map((door) => (
                <li key={door.href}>
                  <Link
                    href={door.href}
                    style={{
                      color: SAND_TEXT,
                      textDecoration: "none",
                      fontSize: 15,
                      borderBottom: `1px solid ${SAND_LINE}`,
                      paddingBottom: 2,
                    }}
                  >
                    {door.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
      )}

      {/* ── Bottom strip — © and the font credit ───────────── */}
      <div
        style={{
          borderTop: `1px solid ${SAND_LINE}`,
          padding: `14px ${GUTTER}`,
          fontFamily: "var(--mono)",
          fontSize: 10,
          letterSpacing: 0.4,
          lineHeight: 1.6,
          color: SAND_DIM,
        }}
      >
        <div
          style={{
            maxWidth: "var(--frame-w)",
            margin: "0 auto",
            display: "flex",
            flexWrap: "wrap",
            gap: "4px 22px",
          }}
        >
          <span>© 2026 Chris Perkins</span>
          <span>
            Title type: Duotone by{" "}
            <a
              href="https://matchandkerosene.com/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: SUN, textDecoration: "underline", textDecorationColor: "rgba(217, 163, 95, 0.55)", textUnderlineOffset: 3 }}
            >
              Match and Kerosene
            </a>
            .
          </span>
        </div>
        {/* Z9: the last line of the page is the story's, not the site's. When it ends on "Governance in Motion" that
            part carries the hero's arrival trail, starting when the reader reaches the footer (Chris, 2026-10-25). */}
        {lastLine && (
          <p style={{ maxWidth: "var(--frame-w)", margin: "12px auto 0", fontFamily: "var(--sans)", fontSize: 14, letterSpacing: 0, lineHeight: 1.5, color: SAND_TEXT, fontStyle: "italic" }}>
            {(() => {
              const m = /^(.*?)(Governance in Motion)(\.?)$/.exec(lastLine);
              return m ? <Trail head={m[1]} mark={m[2]} after={m[3]} onView /> : lastLine;
            })()}
          </p>
        )}
      </div>
    </footer>
  );
}
